// TODO: vitest component test (pages are covered by Playwright e2e)
import { useState } from 'react'
import { useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Car, CalendarDays, LogIn, LogOut, Trash2, Plus } from 'lucide-react'
import { formatBaht } from '@/lib/money'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import {
  Button,
  Card,
  Badge,
  EmptyState,
  SetupGuide,
  Skeleton,
  DataTable,
  Modal,
  ConfirmDialog,
  Input,
  Select,
  Textarea,
  Pagination,
} from '@/components/ui'
import type { Column } from '@/components/ui/DataTable'
import { useDailyLogs, useCreateDailyLog, useUpdateDailyLog, useDeleteDailyLog } from '@/hooks/useDailyParkingLogs'
import { useFeeConfigs } from '@/hooks/useParkingFeeConfigs'
import { useProperty } from '@/hooks/useProperties'
import { useZones } from '@/hooks/useParkingZones'
import { useTimeFormatter } from '@/hooks/useDateFormatter'
import type { DailyParkingLog, VehicleType, DailyParkingPaymentMethod } from '@/types/parking'

// Zod schema for the entry form
const dailyLogSchema = z.object({
  license_plate: z.string().min(1),
  vehicle_type: z.enum(['car', 'motorcycle', 'pickup', 'bicycle', 'van']),
  fee: z.coerce.number().min(0),
  payment_method: z.enum(['cash', 'transfer', 'promptpay']).optional(),
  parker_name: z.string().optional(),
  parker_phone: z.string().optional(),
  entered_at: z.string().min(1),
  notes: z.string().optional(),
})

type DailyLogFormValues = z.infer<typeof dailyLogSchema>

// Format datetime-local value (YYYY-MM-DDTHH:MM)
function toDatetimeLocal(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

// Get today's date string in YYYY-MM-DD format (local time)
function todayString(): string {
  const d = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

export function DailyParkingLogPage() {
  const { t } = useTranslation('parking')
  const { t: tc } = useTranslation('common')
  const formatTime = useTimeFormatter()
  const { propertyId: propertyIdParam } = useParams<{ propertyId: string }>()
  const propertyId = Number(propertyIdParam)

  // Date filter — default today
  const [selectedDate, setSelectedDate] = useState(todayString)
  const [page, setPage] = useState(1)

  // Modal state
  const [isEntryModalOpen, setIsEntryModalOpen] = useState(false)
  const [exitTarget, setExitTarget] = useState<DailyParkingLog | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<DailyParkingLog | null>(null)

  // Data hooks
  const { data: property } = useProperty(propertyId)
  const { data: feeConfigsData } = useFeeConfigs(propertyId)
  const feeConfigs = feeConfigsData?.data ?? []

  const { data, isLoading, isFetching, error } = useDailyLogs(propertyId, {
    page,
    from: selectedDate,
    to: selectedDate,
  })

  // Unfiltered query to decide between setup guide and empty state
  const { data: totalData } = useDailyLogs(propertyId, { page: 1 })
  const totalCount = totalData?.meta?.total_count ?? 0

  // Prerequisite data
  const { data: zonesData } = useZones(propertyId)
  const hasZones = (zonesData?.data?.length ?? 0) > 0
  const hasFeeConfigs = feeConfigs.length > 0

  const createDailyLog = useCreateDailyLog()
  const updateDailyLog = useUpdateDailyLog()
  const deleteDailyLog = useDeleteDailyLog()

  const logs = data?.data ?? []

  // Daily revenue: sum of all logs currently displayed (for selected date)
  const totalRevenue = logs.reduce((sum, log) => sum + (log.fee ?? 0), 0)

  // Form
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<DailyLogFormValues>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(dailyLogSchema) as any,
    defaultValues: {
      license_plate: '',
      vehicle_type: 'car',
      fee: 0,
      payment_method: undefined,
      parker_name: '',
      parker_phone: '',
      entered_at: toDatetimeLocal(new Date()),
      notes: '',
    },
  })

  // Auto-populate fee from config when vehicle_type changes
  const handleVehicleTypeChange = (vehicleType: string) => {
    const config = feeConfigs.find((c) => c.vehicle_type === vehicleType)
    if (config) {
      setValue('fee', config.external_daily_fee)
    }
  }

  const handleOpenEntryModal = () => {
    reset({
      license_plate: '',
      vehicle_type: 'car',
      fee: feeConfigs.find((c) => c.vehicle_type === 'car')?.external_daily_fee ?? 0,
      payment_method: undefined,
      parker_name: '',
      parker_phone: '',
      entered_at: toDatetimeLocal(new Date()),
      notes: '',
    })
    setIsEntryModalOpen(true)
  }

  const onSubmitEntry = async (values: DailyLogFormValues) => {
    await createDailyLog.mutateAsync({
      property_id: propertyId,
      license_plate: values.license_plate,
      vehicle_type: values.vehicle_type as VehicleType,
      fee: values.fee,
      payment_method: values.payment_method as DailyParkingPaymentMethod | undefined,
      parker_name: values.parker_name || undefined,
      parker_phone: values.parker_phone || undefined,
      entered_at: new Date(values.entered_at).toISOString(),
      notes: values.notes || undefined,
    })
    setIsEntryModalOpen(false)
    reset()
  }

  const handleExit = async () => {
    if (!exitTarget) return
    await updateDailyLog.mutateAsync({
      id: exitTarget.id,
      exited_at: new Date().toISOString(),
    })
    setExitTarget(null)
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    await deleteDailyLog.mutateAsync(deleteTarget.id)
    setDeleteTarget(null)
  }

  const getVehicleTypeBadge = (type: VehicleType) => {
    const label = t(`vehicles.types.${type}`)
    switch (type) {
      case 'car':
        return <Badge variant="info">{label}</Badge>
      case 'motorcycle':
        return <Badge variant="warning">{label}</Badge>
      case 'pickup':
        return <Badge variant="ghost">{label}</Badge>
      case 'bicycle':
        return <Badge variant="success">{label}</Badge>
      case 'van':
        return <Badge variant="ghost">{label}</Badge>
      default:
        return <Badge variant="ghost">{label}</Badge>
    }
  }

  const getPaymentMethodBadge = (method: DailyParkingPaymentMethod | null) => {
    if (!method) return null
    switch (method) {
      case 'cash':
        return <Badge variant="success">{t('daily_logs.payment_methods.cash')}</Badge>
      case 'transfer':
        return <Badge variant="info">{t('daily_logs.payment_methods.transfer')}</Badge>
      case 'promptpay':
        return <Badge variant="warning">{t('daily_logs.payment_methods.promptpay')}</Badge>
      default:
        return null
    }
  }

  const columns: Column<DailyParkingLog>[] = [
    {
      key: 'license_plate',
      header: t('daily_logs.license_plate'),
      render: (log) => <span className="font-medium font-mono">{log.license_plate}</span>,
    },
    {
      key: 'vehicle_type',
      header: t('daily_logs.vehicle_type'),
      render: (log) => getVehicleTypeBadge(log.vehicle_type),
    },
    {
      key: 'parker_name',
      header: t('daily_logs.parker_name'),
      render: (log) => <span>{log.parker_name || '-'}</span>,
    },
    {
      key: 'fee',
      header: t('daily_logs.fee'),
      render: (log) => <span className="font-medium">{formatBaht(log.fee)}</span>,
    },
    {
      key: 'payment_method',
      header: t('daily_logs.payment_method'),
      render: (log) => getPaymentMethodBadge(log.payment_method) ?? <span className="text-base-content/40">-</span>,
    },
    {
      key: 'entered_at',
      header: t('daily_logs.entered_at'),
      render: (log) => <span>{formatTime(log.entered_at)}</span>,
    },
    {
      key: 'exited_at',
      header: t('daily_logs.exited_at'),
      render: (log) =>
        log.exited_at ? (
          <span>{formatTime(log.exited_at)}</span>
        ) : (
          <Badge variant="warning">{t('daily_logs.still_parked')}</Badge>
        ),
    },
    {
      key: 'actions',
      header: '',
      render: (log) => (
        <div className="flex gap-1 justify-end">
          {!log.exited_at && (
            <Button
              size="sm"
              variant="ghost"
              className="text-success"
              title={t('daily_logs.exit')}
              leftIcon={<LogOut className="w-4 h-4" />}
              onClick={(e) => {
                e.stopPropagation()
                setExitTarget(log)
              }}
            />
          )}
          <Button
            size="sm"
            variant="ghost"
            className="text-error"
            title={tc('actions.delete')}
            leftIcon={<Trash2 className="w-4 h-4" />}
            onClick={(e) => {
              e.stopPropagation()
              setDeleteTarget(log)
            }}
          />
        </div>
      ),
    },
  ]

  const vehicleTypeOptions = [
    { value: 'car', label: t('vehicles.types.car') },
    { value: 'motorcycle', label: t('vehicles.types.motorcycle') },
    { value: 'pickup', label: t('vehicles.types.pickup') },
    { value: 'bicycle', label: t('vehicles.types.bicycle') },
    { value: 'van', label: t('vehicles.types.van') },
  ]

  const paymentMethodOptions = [
    { value: '', label: '-' },
    { value: 'cash', label: t('daily_logs.payment_methods.cash') },
    { value: 'transfer', label: t('daily_logs.payment_methods.transfer') },
    { value: 'promptpay', label: t('daily_logs.payment_methods.promptpay') },
  ]

  return (
    <>
      <div className="space-y-6">
        <Breadcrumbs
          items={[
            { label: tc('nav.properties'), href: '/properties' },
            { label: property?.name ?? '...', href: `/properties/${propertyId}` },
            { label: t('daily_logs.title') },
          ]}
        />

        <PageHeader
          title={t('daily_logs.title')}
          description={
            <span className="text-base-content/60">
              {t('daily_logs.daily_total')}:{' '}
              <span className="font-semibold text-base-content">{formatBaht(totalRevenue)}</span>
            </span>
          }
          isRefreshing={isFetching && !isLoading}
          actions={
            <Button
              size="sm"
              leftIcon={<Plus className="w-4 h-4" />}
              onClick={handleOpenEntryModal}
            >
              {t('daily_logs.add')}
            </Button>
          }
        />

        {/* Date Filter */}
        <Card>
          <div className="flex items-end gap-4">
            <div className="w-48">
              <Input
                label={t('daily_logs.date_label')}
                type="date"
                value={selectedDate}
                onChange={(e) => {
                  setSelectedDate(e.target.value)
                  setPage(1)
                }}
              />
            </div>
          </div>
        </Card>

        {/* Content */}
        {isLoading ? (
          <Card>
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12" />
              ))}
            </div>
          </Card>
        ) : error ? (
          <Card>
            <EmptyState
              title={tc('errors.loading_failed')}
              description={tc('errors.try_again')}
              action={
                <Button onClick={() => window.location.reload()}>
                  {tc('actions.retry')}
                </Button>
              }
            />
          </Card>
        ) : !logs.length ? (
          <Card>
            {totalCount === 0 ? (
              <SetupGuide
                icon={<CalendarDays className="w-16 h-16" />}
                title={t('setup_guide.daily_logs.title')}
                description={t('setup_guide.daily_logs.description')}
                prerequisites={[
                  {
                    label: t('setup_guide.daily_logs.prereq_zones'),
                    met: hasZones,
                    link: `/properties/${propertyIdParam}/parking/zones`,
                    linkLabel: t('setup_guide.daily_logs.prereq_setup_zones'),
                  },
                  {
                    label: t('setup_guide.daily_logs.prereq_fee_configs'),
                    met: hasFeeConfigs,
                    link: `/properties/${propertyIdParam}/parking-fees`,
                    linkLabel: t('setup_guide.daily_logs.prereq_setup_fees'),
                  },
                ]}
                prerequisitesTitle={t('setup_guide.prerequisites_title')}
                steps={[
                  {
                    title: t('setup_guide.daily_logs.steps.configure_fees.title'),
                    description: t('setup_guide.daily_logs.steps.configure_fees.description'),
                  },
                  {
                    title: t('setup_guide.daily_logs.steps.log_entry.title'),
                    description: t('setup_guide.daily_logs.steps.log_entry.description'),
                  },
                  {
                    title: t('setup_guide.daily_logs.steps.record_exit.title'),
                    description: t('setup_guide.daily_logs.steps.record_exit.description'),
                  },
                ]}
                action={
                  <Button leftIcon={<LogIn className="w-4 h-4" />} onClick={handleOpenEntryModal}>
                    {t('daily_logs.add')}
                  </Button>
                }
              />
            ) : (
              <EmptyState
                icon={<Car className="w-12 h-12" />}
                title={t('daily_logs.empty.title')}
                description={t('daily_logs.empty.description')}
                action={
                  <Button leftIcon={<LogIn className="w-4 h-4" />} onClick={handleOpenEntryModal}>
                    {t('daily_logs.add')}
                  </Button>
                }
              />
            )}
          </Card>
        ) : (
          <Card>
            <DataTable
              columns={columns}
              data={logs}
              keyExtractor={(log) => log.id}
            />
          </Card>
        )}

        {/* Pagination */}
        {data?.meta && data.meta.total_count > 0 && (
          <div className="flex justify-center">
            <Pagination
              currentPage={data.meta.current_page}
              totalPages={data.meta.total_pages}
              totalItems={data.meta.total_count}
              pageSize={data.meta.per_page}
              onPageChange={setPage}
            />
          </div>
        )}
      </div>

      {/* Entry Modal */}
      <Modal
        isOpen={isEntryModalOpen}
        onClose={() => setIsEntryModalOpen(false)}
        title={t('daily_logs.add')}
        size="lg"
        footer={
          <div className="flex gap-2 justify-end w-full">
            <Button
              variant="ghost"
              onClick={() => setIsEntryModalOpen(false)}
              disabled={isSubmitting}
            >
              {tc('actions.cancel')}
            </Button>
            <Button
              type="submit"
              form="daily-log-form"
              loading={isSubmitting}
            >
              {tc('actions.save')}
            </Button>
          </div>
        }
      >
        <form
          id="daily-log-form"
          onSubmit={handleSubmit(onSubmitEntry)}
          className="space-y-4"
        >
          <div className="grid grid-cols-2 gap-4">
            <Input
              label={t('daily_logs.license_plate') + ' *'}
              placeholder={t('daily_logs.license_plate_placeholder')}
              error={errors.license_plate ? t('validation.license_plate_required') : undefined}
              {...register('license_plate')}
            />
            <Select
              label={t('daily_logs.vehicle_type') + ' *'}
              options={vehicleTypeOptions}
              error={errors.vehicle_type ? t('validation.vehicle_type_required') : undefined}
              {...register('vehicle_type', {
                onChange: (e) => handleVehicleTypeChange(e.target.value),
              })}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input
              label={t('daily_logs.fee') + ' *'}
              type="number"
              min="0"
              step="1"
              error={errors.fee?.message}
              {...register('fee')}
            />
            <Select
              label={t('daily_logs.payment_method')}
              options={paymentMethodOptions}
              {...register('payment_method')}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input
              label={t('daily_logs.parker_name')}
              {...register('parker_name')}
            />
            <Input
              label={t('daily_logs.parker_phone')}
              type="tel"
              {...register('parker_phone')}
            />
          </div>

          <Input
            label={t('daily_logs.entered_at') + ' *'}
            type="datetime-local"
            error={errors.entered_at?.message}
            {...register('entered_at')}
          />

          <Textarea
            label={t('daily_logs.notes')}
            rows={2}
            {...register('notes')}
          />
        </form>
      </Modal>

      {/* Exit Confirm Dialog */}
      <ConfirmDialog
        isOpen={!!exitTarget}
        onClose={() => setExitTarget(null)}
        onConfirm={handleExit}
        title={t('daily_logs.exit')}
        message={t('daily_logs.exit_confirm')}
        confirmLabel={t('daily_logs.exit')}
        cancelLabel={tc('actions.cancel')}
        variant="info"
        loading={updateDailyLog.isPending}
      />

      {/* Delete Confirm Dialog */}
      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title={tc('actions.delete')}
        message={t('daily_logs.delete_confirm')}
        confirmLabel={tc('actions.delete')}
        cancelLabel={tc('actions.cancel')}
        variant="error"
        loading={deleteDailyLog.isPending}
      />
    </>
  )
}
